import React, { useState, useEffect, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from "reactstrap";
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import apiService from "../../services/apiService";

const card = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid var(--vz-border-color)",
  background: "var(--vz-input-bg, var(--vz-secondary-bg, #fff))",
  color: "var(--vz-body-color)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

const alertColors = {
  success: { color: "var(--vz-success)", bg: "rgba(var(--vz-success-rgb),.1)", border: "rgba(var(--vz-success-rgb),.3)", icon: "check-circle" },
  danger:  { color: "var(--vz-danger)",  bg: "rgba(var(--vz-danger-rgb),.1)",  border: "rgba(var(--vz-danger-rgb),.3)",  icon: "alert-circle"  },
  warning: { color: "var(--vz-warning)", bg: "rgba(var(--vz-warning-rgb),.1)", border: "rgba(var(--vz-warning-rgb),.3)", icon: "alert-triangle" },
  info:    { color: "var(--vz-info)",    bg: "rgba(var(--vz-info-rgb),.1)",    border: "rgba(var(--vz-info-rgb),.3)",    icon: "info"           },
};

const validateDomain = (input) => {
  const clean = input.trim().toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split("/")[0];
  const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
  return domainRegex.test(clean) ? clean : null;
};

const WebFilteringPolicies = () => {
  const [activeTab, setActiveTab] = useState("block");
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);

  // Block URL form
  const [urlInput, setUrlInput]           = useState("");
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [blocking, setBlocking]           = useState(false);

  // Unblock URL form
  const [unblockUrl, setUnblockUrl]         = useState("");
  const [unblockAgents, setUnblockAgents]   = useState([]);
  const [unblocking, setUnblocking]         = useState(false);
  const [confirmOpen, setConfirmOpen]       = useState(false);

  // Inline alert
  const [alert, setAlert] = useState(null);

  // History (DB-backed)
  const [actionLog,     setActionLog]     = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Agent groups
  const [groups,             setGroups]             = useState([]);
  const [selectedGroup,      setSelectedGroup]      = useState(null);
  const [selectedUnblockGroup, setSelectedUnblockGroup] = useState(null);

  // Clear history
  const [clearingHistory,    setClearingHistory]    = useState(false);
  const [clearConfirmOpen,   setClearConfirmOpen]   = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await apiService.get("/api/web-blocking/history");
      if (res?.success) setActionLog(res.history || []);
    } catch { /* ignore */ }
    finally { setHistoryLoading(false); }
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const res = await apiService.get("/api/agent-groups");
      if (res?.success) setGroups(res.groups || []);
    } catch { /* ignore */ }
  }, []);

  const clearHistory = async () => {
    setClearConfirmOpen(false);
    setClearingHistory(true);
    try {
      const res = await apiService.delete("/api/web-blocking/history");
      if (res?.success) {
        setActionLog([]);
        showAlert("success", "Blocking history cleared.");
      } else {
        showAlert("danger", res?.error || "Failed to clear history.");
      }
    } catch (err) {
      showAlert("danger", err?.message || "Failed to reach API.");
    } finally {
      setClearingHistory(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const loadAgents = useCallback(async () => {
    try {
      setLoadingAgents(true);
      const res = await apiService.get("/api/wazuh/agents");
      setAgents(res.agents || []);
    } catch {
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  useEffect(() => { loadAgents(); }, [loadAgents]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  const onlineCount = agents.filter((a) => (a.status || "").toLowerCase().includes("active")).length;

  const agentOptions = agents.map((a) => ({
    value: a.id,
    label: `${a.name || a.id} (${a.ip || "—"}) — ${a.status}`,
  }));

  const handleBlock = async () => {
    const domain = validateDomain(urlInput);
    if (!domain) {
      showAlert("warning", "Enter a valid domain, e.g. facebook.com");
      return;
    }
    if (!selectedAgents.length) {
      showAlert("warning", "Select at least one agent.");
      return;
    }
    setBlocking(true);
    try {
      // Send to ALL agents - backend will queue for offline agents
      const agentIds    = selectedAgents.map((o) => o.value);
      const agentLabels = selectedAgents.map((o) => o.label);

      const res = await apiService.post("/api/web-blocking/block-multiple", {
        urls: [domain],
        agent_ids: agentIds,
        agent_labels: agentLabels,
      });
      if (res?.success) {
        const onlineCount = res.online_executed || 0;
        const queuedCount = res.offline_queued || 0;

        let msg = `Blocked "${domain}" on ${agentIds.length} agent(s)`;
        if (queuedCount > 0) {
          msg += ` (${onlineCount} online, ${queuedCount} queued for offline agents)`;
        }
        showAlert("success", msg);
        await loadHistory();
        setUrlInput("");
        setSelectedAgents([]);
      } else {
        showAlert("danger", res?.error || "Block command failed.");
      }
    } catch (err) {
      showAlert("danger", err?.message || "Failed to reach API.");
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async () => {
    const domain = validateDomain(unblockUrl);
    if (!domain) {
      showAlert("warning", "Enter a valid domain, e.g. facebook.com");
      return;
    }
    if (!unblockAgents.length) {
      showAlert("warning", "Select at least one agent.");
      return;
    }
    setConfirmOpen(true);
  };

  const confirmUnblock = async () => {
    setConfirmOpen(false);
    const domain = validateDomain(unblockUrl);
    const agentIds    = unblockAgents.map((o) => o.value);
    const agentLabels = unblockAgents.map((o) => o.label);
    setUnblocking(true);
    try {
      const res = await apiService.post("/api/unblock-url", {
        url: domain,
        agent_ids: agentIds,
        agent_labels: agentLabels,
      });
      if (res?.success) {
        const onlineCount = res.online_executed || res.count || 0;
        const queuedCount = res.offline_queued || 0;

        let msg = `Unblocked "${domain}" on ${agentIds.length} agent(s)`;
        if (queuedCount > 0) {
          msg += ` (${onlineCount} online, ${queuedCount} queued for offline agents)`;
        }
        showAlert("success", msg);
        await loadHistory();
        setUnblockUrl("");
        setUnblockAgents([]);
      } else {
        showAlert("danger", res?.error || "Unblock command failed.");
      }
    } catch (err) {
      showAlert("danger", err?.message || "Failed to reach API.");
    } finally {
      setUnblocking(false);
    }
  };

  const groupOptions = groups.map((g) => ({
    value: g.id,
    label: g.name,
    agentCount: g.agent_ids.length,
    agentIds: g.agent_ids,
  }));

  const handleGroupChange = (option) => {
    setSelectedGroup(option);
    if (option) {
      const matched = agentOptions.filter((o) => option.agentIds.includes(o.value));
      setSelectedAgents(matched);
    } else {
      setSelectedAgents([]);
    }
  };

  const handleUnblockGroupChange = (option) => {
    setSelectedUnblockGroup(option);
    if (option) {
      const matched = agentOptions.filter((o) => option.agentIds.includes(o.value));
      setUnblockAgents(matched);
    } else {
      setUnblockAgents([]);
    }
  };

  const tabs = [
    { id: "block",   label: "Block URL",   icon: "slash"  },
    { id: "unblock", label: "Unblock URL", icon: "unlock" },
    { id: "history", label: "History",     icon: "clock"  },
  ];

  const tabBtn = (tab) => {
    const active = activeTab === tab.id;
    return (
      <button
        key={tab.id}
        onClick={() => setActiveTab(tab.id)}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
          fontWeight: active ? 700 : 500, fontSize: 13,
          background: active ? "var(--vz-primary)" : "transparent",
          color: active ? "#fff" : "var(--vz-secondary-color)",
          transition: "all .15s",
        }}
      >
        <FeatherIcon icon={tab.icon} size={14} />
        {tab.label}
      </button>
    );
  };

  const selectStyles = {
    control: (b, s) => ({
      ...b,
      background: "var(--vz-input-bg, var(--vz-secondary-bg, #fff))",
      borderColor: s.isFocused ? "var(--vz-primary)" : "var(--vz-border-color)",
      boxShadow: "none",
      minHeight: 38,
      fontSize: 13,
    }),
    menu: (b) => ({ ...b, background: "var(--vz-secondary-bg, #fff)", zIndex: 9999 }),
    option: (b, s) => ({
      ...b,
      background: s.isSelected
        ? "var(--vz-primary)"
        : s.isFocused
        ? "rgba(var(--vz-primary-rgb),.08)"
        : "transparent",
      color: s.isSelected ? "#fff" : "var(--vz-body-color)",
      fontSize: 13,
    }),
    multiValue: (b) => ({ ...b, background: "var(--vz-secondary-bg, #fff)", border: "1px solid var(--vz-border-color)", borderRadius: 6 }),
    multiValueLabel: (b) => ({ ...b, color: "var(--vz-body-color)", fontSize: 12 }),
    multiValueRemove: (b) => ({ ...b, color: "var(--vz-secondary-color)", ":hover": { background: "rgba(var(--vz-danger-rgb),.1)", color: "var(--vz-danger)" } }),
    placeholder: (b) => ({ ...b, color: "var(--vz-secondary-color)", fontSize: 13 }),
    input: (b) => ({ ...b, color: "var(--vz-body-color)" }),
  };

  return (
    <>
      <div style={card}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <span style={{ fontSize: 15, fontWeight: 700, color: "var(--vz-body-color)" }}>Web Filtering</span>
            <p style={{ color: "var(--vz-secondary-color)", fontSize: 12, margin: 0 }}>
              Block or unblock domains across managed agents instantly
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--vz-success)" }} />
              <span style={{ fontSize: 12, color: "var(--vz-secondary-color)", fontWeight: 600 }}>
                {loadingAgents ? "Loading..." : `${onlineCount} online / ${agents.length} total`}
              </span>
            </div>
            {actionLog.length > 0 && (
              <button
                onClick={() => setClearConfirmOpen(true)}
                disabled={clearingHistory}
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, border: "1px solid rgba(var(--vz-danger-rgb),.3)", background: "rgba(var(--vz-danger-rgb),.06)", color: "var(--vz-danger)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                <FeatherIcon icon="trash-2" size={12} />
                Clear History
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ padding: "10px 16px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", gap: 4, background: "rgba(var(--vz-primary-rgb),.02)" }}>
          {tabs.map(tabBtn)}
        </div>

        {/* Alert */}
        {alert && (() => {
          const c = alertColors[alert.type] || alertColors.info;
          return (
            <div style={{ margin: "12px 20px 0", padding: "10px 14px", borderRadius: 8, background: c.bg, border: `1px solid ${c.border}`, color: c.color, display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <FeatherIcon icon={c.icon} size={14} />
              {alert.message}
            </div>
          );
        })()}

        {/* Body */}
        <div style={{ padding: 20 }}>

          {/* ── Block URL tab ── */}
          {activeTab === "block" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 6 }}>
                  Domain to Block
                </label>
                <input
                  style={inputStyle}
                  placeholder="e.g. facebook.com or reddit.com"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleBlock()}
                  disabled={blocking}
                />
                <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: "4px 0 0" }}>
                  Enter the domain only — https:// and www. are stripped automatically.
                </p>
              </div>

              {/* Group Policy selector */}
              {groups.length > 0 && (
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 6 }}>
                    Group Policy
                  </label>
                  <Select
                    isClearable
                    options={groupOptions}
                    value={selectedGroup}
                    onChange={handleGroupChange}
                    placeholder="Select a group to target all agents in it…"
                    styles={selectStyles}
                    isDisabled={blocking}
                    formatOptionLabel={(opt) => (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontWeight: 600 }}>{opt.label}</span>
                        <span style={{ fontSize: 11, color: "var(--vz-secondary-color)", marginLeft: 8 }}>{opt.agentCount} agent{opt.agentCount !== 1 ? "s" : ""}</span>
                      </div>
                    )}
                  />
                </div>
              )}

              {/* Divider */}
              {groups.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, height: 1, background: "var(--vz-border-color)" }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em" }}>or select individually</span>
                  <div style={{ flex: 1, height: 1, background: "var(--vz-border-color)" }} />
                </div>
              )}

              {/* Individual agent selector */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em", margin: 0 }}>
                    Target Agents
                  </label>
                  {!loadingAgents && (
                    <button
                      onClick={() => selectedAgents.length === agentOptions.length ? setSelectedAgents([]) : setSelectedAgents(agentOptions)}
                      disabled={blocking}
                      style={{ fontSize: 11, fontWeight: 600, color: "var(--vz-primary)", background: "transparent", border: "1px solid var(--vz-primary)", borderRadius: 6, padding: "2px 10px", cursor: "pointer" }}>
                      {selectedAgents.length === agentOptions.length ? "Clear All" : "Select All"}
                    </button>
                  )}
                </div>
                {loadingAgents ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--vz-secondary-color)", fontSize: 13 }}>
                    <Spinner size="sm" color="primary" /> Loading agents...
                  </div>
                ) : (
                  <Select
                    isMulti
                    options={agentOptions}
                    value={selectedAgents}
                    onChange={(v) => { setSelectedAgents(v); setSelectedGroup(null); }}
                    placeholder="Search and select agents…"
                    styles={selectStyles}
                    isDisabled={blocking}
                  />
                )}
              </div>

              <button
                onClick={handleBlock}
                disabled={blocking || !urlInput.trim() || !selectedAgents.length}
                style={{
                  alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 24px", borderRadius: 8, border: "none", cursor: blocking ? "not-allowed" : "pointer",
                  background: blocking || !urlInput.trim() || !selectedAgents.length ? "rgba(var(--vz-danger-rgb),.4)" : "var(--vz-danger)",
                  color: "#fff", fontWeight: 700, fontSize: 13,
                }}
              >
                {blocking ? <Spinner size="sm" /> : <FeatherIcon icon="slash" size={14} />}
                {blocking ? "Blocking..." : `Block on ${selectedAgents.length || 0} Agent${selectedAgents.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          )}

          {/* ── Unblock URL tab ── */}
          {activeTab === "unblock" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 6 }}>
                  Domain to Unblock
                </label>
                <input
                  style={inputStyle}
                  placeholder="e.g. facebook.com"
                  value={unblockUrl}
                  onChange={(e) => setUnblockUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleUnblock()}
                  disabled={unblocking}
                />
              </div>

              {/* Group Policy selector */}
              {groups.length > 0 && (
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 6 }}>
                    Group Policy
                  </label>
                  <Select
                    isClearable
                    options={groupOptions}
                    value={selectedUnblockGroup}
                    onChange={handleUnblockGroupChange}
                    placeholder="Select a group to target all agents in it…"
                    styles={selectStyles}
                    isDisabled={unblocking}
                    formatOptionLabel={(opt) => (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontWeight: 600 }}>{opt.label}</span>
                        <span style={{ fontSize: 11, color: "var(--vz-secondary-color)", marginLeft: 8 }}>{opt.agentCount} agent{opt.agentCount !== 1 ? "s" : ""}</span>
                      </div>
                    )}
                  />
                </div>
              )}

              {/* Divider */}
              {groups.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, height: 1, background: "var(--vz-border-color)" }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em" }}>or select individually</span>
                  <div style={{ flex: 1, height: 1, background: "var(--vz-border-color)" }} />
                </div>
              )}

              {/* Individual agent selector */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em", margin: 0 }}>
                    Target Agents
                  </label>
                  {!loadingAgents && (
                    <button
                      onClick={() => unblockAgents.length === agentOptions.length ? setUnblockAgents([]) : setUnblockAgents(agentOptions)}
                      disabled={unblocking}
                      style={{ fontSize: 11, fontWeight: 600, color: "var(--vz-primary)", background: "transparent", border: "1px solid var(--vz-primary)", borderRadius: 6, padding: "2px 10px", cursor: "pointer" }}>
                      {unblockAgents.length === agentOptions.length ? "Clear All" : "Select All"}
                    </button>
                  )}
                </div>
                {loadingAgents ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--vz-secondary-color)", fontSize: 13 }}>
                    <Spinner size="sm" color="primary" /> Loading agents...
                  </div>
                ) : (
                  <Select
                    isMulti
                    options={agentOptions}
                    value={unblockAgents}
                    onChange={(v) => { setUnblockAgents(v); setSelectedUnblockGroup(null); }}
                    placeholder="Search and select agents…"
                    styles={selectStyles}
                    isDisabled={unblocking}
                  />
                )}
              </div>

              <button
                onClick={handleUnblock}
                disabled={unblocking || !unblockUrl.trim() || !unblockAgents.length}
                style={{
                  alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 24px", borderRadius: 8, border: "none", cursor: unblocking ? "not-allowed" : "pointer",
                  background: unblocking || !unblockUrl.trim() || !unblockAgents.length ? "rgba(var(--vz-success-rgb),.4)" : "var(--vz-success)",
                  color: "#fff", fontWeight: 700, fontSize: 13,
                }}
              >
                {unblocking ? <Spinner size="sm" /> : <FeatherIcon icon="unlock" size={14} />}
                {unblocking ? "Unblocking..." : `Unblock on ${unblockAgents.length || 0} Agent${unblockAgents.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          )}

          {/* ── History tab ── */}
          {activeTab === "history" && (
            <div>
              {historyLoading ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <Spinner size="sm" color="primary" />
                </div>
              ) : actionLog.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "var(--vz-secondary-color)" }}>
                  <FeatherIcon icon="clock" size={32} style={{ opacity: 0.25, display: "block", margin: "0 auto 10px" }} />
                  <p style={{ margin: 0, fontSize: 13 }}>No actions recorded yet.</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {actionLog.map((entry) => {
                    const isBlock = entry.action === "block";
                    const c = isBlock ? alertColors.danger : alertColors.success;
                    const labels = entry.agent_labels?.join(", ") || entry.agent_ids?.join(", ") || "";
                    const time = entry.created_at
                      ? new Date(entry.created_at).toLocaleTimeString()
                      : "";
                    return (
                      <div key={entry.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 14px", borderRadius: 8, background: c.bg, border: `1px solid ${c.border}` }}>
                        <FeatherIcon icon={isBlock ? "slash" : "unlock"} size={14} style={{ color: c.color, marginTop: 2, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: c.color }}>
                            {isBlock ? "Blocked" : "Unblocked"}: <code style={{ background: "transparent", color: c.color }}>{entry.domain}</code>
                          </div>
                          <div style={{ fontSize: 11, color: "var(--vz-secondary-color)", marginTop: 2, wordBreak: "break-word" }}>
                            {labels}
                          </div>
                        </div>
                        <span style={{ fontSize: 11, color: "var(--vz-secondary-color)", flexShrink: 0 }}>{time}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "10px 20px", borderTop: "1px solid var(--vz-border-color)", background: "rgba(var(--vz-primary-rgb),.02)", display: "flex", alignItems: "center", gap: 6 }}>
          <FeatherIcon icon="zap" size={11} style={{ color: "var(--vz-success)" }} />
          <span style={{ fontSize: 11, color: "var(--vz-secondary-color)" }}>
            Commands are delivered instantly to managed agents.
          </span>
        </div>
      </div>

      {/* Confirm clear history modal */}
      <Modal isOpen={clearConfirmOpen} toggle={() => setClearConfirmOpen(false)} centered size="sm">
        <ModalHeader toggle={() => setClearConfirmOpen(false)} style={{ borderBottom: "1px solid var(--vz-border-color)" }}>
          Clear History
        </ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <FeatherIcon icon="alert-triangle" size={20} style={{ color: "var(--vz-warning)", flexShrink: 0, marginTop: 2 }} />
            <div>
              <p style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 600, color: "var(--vz-body-color)" }}>
                Clear all blocking history?
              </p>
              <p style={{ margin: 0, fontSize: 13, color: "var(--vz-secondary-color)" }}>
                This permanently deletes all {actionLog.length} record{actionLog.length !== 1 ? "s" : ""} from the database. This cannot be undone.
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter style={{ borderTop: "1px solid var(--vz-border-color)", gap: 8 }}>
          <button onClick={() => setClearConfirmOpen(false)}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid var(--vz-border-color)", background: "transparent", color: "var(--vz-secondary-color)", cursor: "pointer", fontSize: 13 }}>
            Cancel
          </button>
          <button onClick={clearHistory}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "none", background: "var(--vz-danger)", color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
            <FeatherIcon icon="trash-2" size={13} /> Clear All
          </button>
        </ModalFooter>
      </Modal>

      {/* Confirm unblock modal */}
      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)} centered size="sm">
        <ModalHeader toggle={() => setConfirmOpen(false)} style={{ borderBottom: "1px solid var(--vz-border-color)" }}>
          Confirm Unblock
        </ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <FeatherIcon icon="alert-triangle" size={20} style={{ color: "var(--vz-warning)", flexShrink: 0, marginTop: 2 }} />
            <div>
              <p style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600, color: "var(--vz-body-color)" }}>
                Unblock <code>{validateDomain(unblockUrl) || unblockUrl}</code>?
              </p>
              <p style={{ margin: 0, fontSize: 13, color: "var(--vz-secondary-color)" }}>
                This will re-allow access to this domain on {unblockAgents.length} agent(s).
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter style={{ borderTop: "1px solid var(--vz-border-color)", gap: 8 }}>
          <button onClick={() => setConfirmOpen(false)}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid var(--vz-border-color)", background: "transparent", color: "var(--vz-secondary-color)", cursor: "pointer", fontSize: 13 }}>
            Cancel
          </button>
          <button onClick={confirmUnblock}
            style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "var(--vz-success)", color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            <FeatherIcon icon="unlock" size={13} /> Unblock
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default WebFilteringPolicies;